/**
 * Sticky add to cart bar.
 *
 * Three jobs, and deliberately nothing more:
 *
 *   1. Decide when to show the bar. An IntersectionObserver watches the real
 *      add-to-cart button; the bar is shown whenever that button is off screen,
 *      above OR below. On a phone the real button starts below the fold, so the
 *      bar is there from the first screen. No scroll handlers anywhere.
 *      Until the shopper has picked a size, the bar reads "Select size" and
 *      takes them to the size buttons instead of adding the pre-selected size.
 *
 *   2. Stay in sync. Price, compare price, variant name and thumbnail come from
 *      the theme's own `variant:change` event. The enabled / sold-out state and
 *      the loading spinner are mirrored straight off the real button, which is
 *      the only thing that actually knows.
 *
 *   3. Hand the add-to-cart back to the theme. The button here submits the real
 *      product form, so theme.js's ProductForm does the AJAX add, the loading
 *      state and the cart drawer exactly as it would for the main button. There
 *      is no /cart/add call in this file on purpose.
 *
 * Once the bar has been shown for the first time its height is written to
 * --sticky-atc-offset on :root; the stylesheet turns that into bottom padding
 * on the page so the footer's last row can be scrolled clear of the bar. It is
 * set at the moment the bar appears — far below the viewport at that point — so
 * nothing on screen moves.
 */
(function () {
  var reduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(bar) {
    if (!bar || bar.dataset.satcReady === 'true') return;

    var productId = bar.getAttribute('data-product-id');

    // Variant ids belonging to THIS product. `variant:change` fires on document
    // for every product form on the page, quick-view modals included, and
    // carries no section id — so this is how we know an event is ours.
    var ids = Object.create(null);
    (bar.getAttribute('data-variant-ids') || '').split(',').forEach(function (id) {
      if (id) ids[id] = true;
    });

    // The real product section, never a quick-view copy of it.
    var section = null;
    var candidates = document.querySelectorAll(
      '.product-section[data-product-id="' + productId + '"]'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (
        candidates[i].getAttribute('data-modal') !== 'true' &&
        !candidates[i].closest('[id^="QuickShopModal"]')
      ) {
        section = candidates[i];
        break;
      }
    }
    if (!section) return;

    var realBtn = section.querySelector('[data-add-to-cart]');
    var realBtnText = section.querySelector('[data-add-to-cart-text]');
    var form = realBtn && realBtn.closest('form.product-single__form');
    if (!realBtn || !form) return;

    var btn = bar.querySelector('[data-satc-submit]');
    if (!btn) return;

    var labelEl = bar.querySelector('[data-satc-label]');
    var priceEl = bar.querySelector('[data-satc-price]');
    var compareEl = bar.querySelector('[data-satc-compare]');
    var variantEl = bar.querySelector('[data-satc-variant]');
    var thumbImg = bar.querySelector('[data-satc-thumb] img');
    var changeBtn = bar.querySelector('[data-satc-change]');

    // Not the rendered text: on a sold-out product that already reads
    // "Sold out", and we need the label to go back to when a variant that IS
    // in stock gets picked.
    var defaultLabel =
      (labelEl && labelEl.getAttribute('data-default-text')) || 'Add to cart';
    var selectLabel = labelEl && labelEl.getAttribute('data-select-text');

    // The theme pre-selects the first available size. The bar is on screen
    // before the size buttons are, so a tap there must not add that size
    // unseen. A ?variant= link (from an ad, say) counts as a choice made.
    var hasChoice = section.querySelectorAll('[data-variant-input]').length > 1;
    var picked = !hasChoice || /[?&]variant=/.test(window.location.search);

    /* ----------------------------------------------------------------------
       Money
       ---------------------------------------------------------------------- */

    // theme.Currency is the theme's own formatter and can return markup (it
    // wraps cents in <sup> when superscript prices are on), so its output goes
    // in as HTML. It is generated from a number and the shop's money format —
    // never from anything a customer typed.
    function money(cents) {
      if (
        window.theme &&
        theme.Currency &&
        typeof theme.Currency.formatMoney === 'function'
      ) {
        return theme.Currency.formatMoney(
          cents,
          theme.settings && theme.settings.moneyFormat
        );
      }
      return null;
    }

    // Fallback, and the source of truth on first load: copy whatever the
    // theme's own price block is currently showing. Also covers currency
    // converters that rewrite those nodes.
    function mirrorPriceFromDom() {
      var themePrice = section.querySelector('[data-product-price]');
      var themeCompare = section.querySelector('[data-compare-price]');
      var themeWrap = section.querySelector('[data-product-price-wrap]');

      if (themePrice && priceEl) priceEl.innerHTML = themePrice.innerHTML;

      if (!compareEl) return;
      var text = themeCompare ? themeCompare.textContent.trim() : '';
      var hidden =
        !text || !themeWrap || themeWrap.classList.contains('hide');
      compareEl.hidden = hidden;
      if (!hidden) compareEl.innerHTML = themeCompare.innerHTML;
    }

    /* ----------------------------------------------------------------------
       Thumbnail
       ---------------------------------------------------------------------- */

    function setThumb(src) {
      if (!thumbImg || !src) return;
      var url = src.indexOf('//') === 0 ? 'https:' + src : src;
      // Ask the CDN for a thumbnail instead of pulling the full-size file.
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=160';
      if (thumbImg.getAttribute('src') === url) return;
      // The srcset was built for the original image; drop it before swapping.
      thumbImg.removeAttribute('srcset');
      thumbImg.removeAttribute('sizes');
      thumbImg.setAttribute('src', url);
    }

    /* ----------------------------------------------------------------------
       State
       ---------------------------------------------------------------------- */

    function applyVariant(variant) {
      if (!variant) return;

      if (variantEl) variantEl.textContent = variant.title || '';

      if (priceEl) {
        var price = money(variant.price);
        if (price === null) mirrorPriceFromDom();
        else priceEl.innerHTML = price;
      }

      if (compareEl) {
        var onSale =
          variant.compare_at_price && variant.compare_at_price > variant.price;
        if (onSale) {
          var compare = money(variant.compare_at_price);
          if (compare !== null) compareEl.innerHTML = compare;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
          compareEl.textContent = '';
        }
      }

      if (variant.featured_image && variant.featured_image.src) {
        setThumb(variant.featured_image.src);
      }
    }

    // Availability and the loading spinner are never decided here — they are
    // read off the button the theme already maintains.
    function syncButton() {
      var disabled = !!realBtn.disabled;
      btn.disabled = disabled;
      btn.classList.toggle('is-loading', realBtn.classList.contains('btn--loading'));

      // While no size is picked, the pre-selected one is not shown either.
      bar.classList.toggle('needs-size', !picked);

      if (!labelEl) return;
      if (disabled) {
        var text = realBtnText ? realBtnText.textContent.trim() : '';
        labelEl.textContent =
          text ||
          (window.theme && theme.strings && theme.strings.soldOut) ||
          'Sold out';
      } else {
        labelEl.textContent = !picked && selectLabel ? selectLabel : defaultLabel;
      }
    }

    var buttonObserver = new MutationObserver(syncButton);
    buttonObserver.observe(realBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });

    function onVariantChange(event) {
      var variant = event && event.detail ? event.detail.variant : null;
      // Another product's form (a quick view, say) — not ours to answer.
      if (variant && !ids[String(variant.id)]) return;
      picked = true;
      applyVariant(variant);
      // The theme updates the real button in the same tick; read it after.
      requestAnimationFrame(syncButton);
    }

    document.addEventListener('variant:change', onVariantChange);

    // Re-picking the size that is already selected fires no variant:change.
    form.addEventListener('change', function (event) {
      if (!event.target || !event.target.matches('[data-variant-input]')) return;
      picked = true;
      syncButton();
    });

    /* ----------------------------------------------------------------------
       Back up to the variant picker, and focused
       ---------------------------------------------------------------------- */

    function isShown(el) {
      return !!el && el.getClientRects().length > 0;
    }

    function goToPicker() {
      var input =
        section.querySelector('.variant-wrapper [data-variant-input]:checked') ||
        section.querySelector('.variant-wrapper [data-variant-input]') ||
        section.querySelector('[data-variant-input]');

      var target = input && input.closest('.variant-wrapper');

      // The swatch app (Swatch King) sets .variant-wrapper to display:none and
      // draws its own buttons, so scrolling to the wrapper went nowhere. Find
      // the app's visible button for the same value and use its group.
      if (!isShown(target)) {
        target = null;
        var value = input ? String(input.value).trim() : '';
        var items = section.querySelectorAll('.swatch-view-item');
        for (var n = 0; n < items.length && value; n++) {
          if (isShown(items[n]) && items[n].textContent.trim() === value) {
            target = items[n].closest('.swatch-single') || items[n];
            break;
          }
        }
      }

      // The form is the worst case but still lands the reader nearby.
      target = target || form;
      if (!target) return;

      target.scrollIntoView({
        behavior: reduced ? 'auto' : 'smooth',
        block: 'center'
      });

      if (!input || !isShown(input)) return;
      try {
        input.focus({ preventScroll: true });
      } catch (err) {
        input.focus();
      }
    }

    /* ----------------------------------------------------------------------
       Add to cart — handed straight back to the theme's own form
       ---------------------------------------------------------------------- */

    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      if (!picked) {
        goToPicker();
        return;
      }

      try {
        if (typeof form.requestSubmit === 'function') {
          if (realBtn && !realBtn.disabled) form.requestSubmit(realBtn);
          else form.requestSubmit();
          return;
        }
      } catch (err) {
        // Older Safari: fall through and click the real submit button instead.
      }

      var submitter = form.querySelector('[type="submit"]');
      if (submitter) submitter.click();
    });

    /* ----------------------------------------------------------------------
       "Change" — back up to the variant picker, and focused
       ---------------------------------------------------------------------- */

    if (changeBtn) {
      changeBtn.addEventListener('click', goToPicker);
    }

    /* ----------------------------------------------------------------------
       Visibility, and the room the bar takes up
       ---------------------------------------------------------------------- */

    var reserved = -1;
    var claimed = false;

    function reserveSpace() {
      var height = Math.round(bar.getBoundingClientRect().height);
      if (height === reserved) return;
      reserved = height;

      // Zero height means the bar is display:none at this width — the section
      // is set to desktop only and the window has just been narrowed, say. Give
      // the space back rather than leaving a strip of dead padding under the
      // footer that nothing occupies.
      if (!height) {
        document.documentElement.style.removeProperty('--sticky-atc-offset');
        return;
      }

      document.documentElement.style.setProperty(
        '--sticky-atc-offset',
        height + 'px'
      );
    }

    var visible = false;

    function setVisible(next) {
      if (next === visible) return;
      visible = next;
      bar.classList.toggle('is-visible', next);
      // Claim the space the first time round, then keep it: taking it back
      // every time the bar hides would make the page jump as you scroll.
      if (next) {
        claimed = true;
        reserveSpace();
      }
    }

    var trigger = new IntersectionObserver(
      function (entries) {
        var entry = entries[entries.length - 1];
        // Any time the real button is off screen. It used to wait until the
        // button had left the TOP, which meant a phone shopper who never
        // scrolled that far never saw a buy button at all.
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    trigger.observe(realBtn);

    // Only re-measures once the bar has actually been shown at least once, so a
    // resize before that costs a frame check and nothing else. Keeps running
    // after a measurement of 0 (bar hidden at this width) so widening the window
    // back to a size where the bar does show restores the padding.
    var resizeFrame = 0;
    function onResize() {
      if (!claimed || resizeFrame) return;
      resizeFrame = requestAnimationFrame(function () {
        resizeFrame = 0;
        // -1, not 0: 0 is a real measurement and would otherwise read as
        // "unchanged" and skip giving the space back.
        reserved = -1;
        reserveSpace();
      });
    }
    window.addEventListener('resize', onResize, { passive: true });

    /* ----------------------------------------------------------------------
       Start from whatever is already on the page
       ---------------------------------------------------------------------- */

    mirrorPriceFromDom();
    syncButton();

    bar.dataset.satcReady = 'true';
    // Kept so a theme-editor reload of the MAIN product section — which leaves
    // this bar in place but detaches everything it points at — can be spotted.
    bar.satcSection = section;

    // Theme editor: tear the observers down when the section is removed.
    bar.satcTeardown = function () {
      trigger.disconnect();
      buttonObserver.disconnect();
      document.removeEventListener('variant:change', onVariantChange);
      window.removeEventListener('resize', onResize);
      document.documentElement.style.removeProperty('--sticky-atc-offset');
    };
  }

  // Re-runs init only when there is something new to bind to: a bar that has
  // not been set up, or one whose product section has been swapped out from
  // under it by the theme editor.
  function boot() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;

    if (bar.dataset.satcReady === 'true') {
      if (bar.satcSection && document.contains(bar.satcSection)) return;
      if (typeof bar.satcTeardown === 'function') bar.satcTeardown();
      bar.dataset.satcReady = 'false';
    }

    init(bar);
  }

  boot();

  document.addEventListener('shopify:section:load', boot);

  document.addEventListener('shopify:section:unload', function (event) {
    var bar = event.target && event.target.querySelector
      ? event.target.querySelector('[data-sticky-atc]')
      : null;
    if (bar && typeof bar.satcTeardown === 'function') bar.satcTeardown();
  });
})();
